import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LOADING_STATE } from "types/general";
import { RESEARCH_SLICE_NAME } from "./constants";
import {
  addWorkItemNote,
  archiveWorkItem,
  assignWorkItem,
  cancelDemand,
  claimWorkItem,
  fetchResearchBoard,
  fetchResearchClientProgress,
  fetchResearchHistory,
  fetchResearchProductivity,
  fetchResearchReferenceData,
  fetchResearchWorkItemDetail,
  previewIntake,
  restoreDemand,
  restoreWorkItem,
  submitIntake,
  transitionWorkItem,
  unclaimWorkItem,
} from "./controllers";
import {
  IResearchClientProgressBucket,
  IResearchCompContext,
  IResearchHistoryEntry,
  IResearchIntakePreview,
  IResearchProductivityDay,
  IResearchReferenceData,
  IResearchSearchResponse,
  IResearchWorkItem,
} from "./types";

export interface IResearchResource<T> {
  activeRequestId: string | null;
  data: T;
  loadingState: LOADING_STATE;
  error: string | null;
}

export interface IResearchState {
  board: IResearchResource<IResearchSearchResponse | null>;
  clientProgress: IResearchResource<IResearchClientProgressBucket[]>;
  productivity: IResearchResource<IResearchProductivityDay[]>;
  referenceData: IResearchResource<IResearchReferenceData | null>;
  selectedWorkItem: IResearchResource<IResearchWorkItem | null>;
  history: IResearchResource<IResearchHistoryEntry[]>;
  intakePreview: IResearchResource<IResearchIntakePreview | null>;
  compContext: IResearchCompContext | null;
}

const resource = <T>(data: T): IResearchResource<T> => ({
  activeRequestId: null,
  data,
  loadingState: LOADING_STATE.idle,
  error: null,
});

export const initialResearchState: IResearchState = {
  board: resource(null),
  clientProgress: resource([]),
  productivity: resource([]),
  referenceData: resource(null),
  selectedWorkItem: resource(null),
  history: resource([]),
  intakePreview: resource(null),
  compContext: null,
};

const researchSlice = createSlice({
  name: RESEARCH_SLICE_NAME,
  initialState: initialResearchState,
  reducers: {
    clearResearchIntakePreview: (state) => {
      state.intakePreview = resource(null);
    },
    clearSelectedResearchWorkItem: (state) => {
      state.selectedWorkItem = resource(null);
      state.history = resource([]);
    },
    setResearchCompContext: (
      state,
      { payload }: PayloadAction<IResearchCompContext | null>
    ) => {
      state.compContext = payload;
    },
    clearResearchCompContext: (state) => {
      state.compContext = null;
    },
  },
  extraReducers: (builder) => {
    const readResources = [
      [fetchResearchBoard, "board"],
      [fetchResearchClientProgress, "clientProgress"],
      [fetchResearchProductivity, "productivity"],
      [fetchResearchReferenceData, "referenceData"],
      [fetchResearchWorkItemDetail, "selectedWorkItem"],
      [fetchResearchHistory, "history"],
      [previewIntake, "intakePreview"],
    ] as const;

    readResources.forEach(([thunk, key]) => {
      builder
        .addCase(thunk.pending, (state, action) => {
          const target = state[key] as IResearchResource<unknown>;
          target.activeRequestId = action.meta.requestId;
          target.loadingState = LOADING_STATE.pending;
          target.error = null;
        })
        .addCase(thunk.fulfilled, (state, action) => {
          const target = state[key] as IResearchResource<unknown>;
          if (target.activeRequestId !== action.meta.requestId) return;
          target.activeRequestId = null;
          target.data = action.payload;
          target.loadingState = LOADING_STATE.fulfilled;
          target.error = null;
        })
        .addCase(thunk.rejected, (state, action) => {
          const target = state[key] as IResearchResource<unknown>;
          if (target.activeRequestId !== action.meta.requestId) return;
          target.activeRequestId = null;
          if (action.meta.aborted) {
            target.loadingState = LOADING_STATE.idle;
            target.error = null;
            return;
          }
          target.loadingState = LOADING_STATE.rejected;
          target.error = action.payload?.message ?? "Request failed";
        });
    });

    builder
      .addCase(submitIntake.pending, (state, action) => {
        state.intakePreview.activeRequestId = action.meta.requestId;
        state.intakePreview.loadingState = LOADING_STATE.pending;
      })
      .addCase(submitIntake.fulfilled, (state, action) => {
        if (state.intakePreview.activeRequestId !== action.meta.requestId)
          return;
        state.intakePreview = resource(null);
      })
      .addCase(submitIntake.rejected, (state, action) => {
        if (state.intakePreview.activeRequestId !== action.meta.requestId)
          return;
        state.intakePreview.activeRequestId = null;
        state.intakePreview.loadingState = LOADING_STATE.rejected;
        state.intakePreview.error = action.payload?.message ?? "Request failed";
      });

    [
      claimWorkItem,
      unclaimWorkItem,
      transitionWorkItem,
      addWorkItemNote,
      assignWorkItem,
      cancelDemand,
      restoreDemand,
      archiveWorkItem,
      restoreWorkItem,
    ].forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state, action) => {
          state.selectedWorkItem.activeRequestId = action.meta.requestId;
        })
        .addCase(thunk.fulfilled, (state, action) => {
          if (state.selectedWorkItem.activeRequestId !== action.meta.requestId)
            return;
          state.selectedWorkItem.activeRequestId = null;
          state.selectedWorkItem.data = action.payload;
          state.selectedWorkItem.loadingState = LOADING_STATE.fulfilled;
          state.selectedWorkItem.error = null;
        })
        .addCase(thunk.rejected, (state, action) => {
          if (state.selectedWorkItem.activeRequestId !== action.meta.requestId)
            return;
          state.selectedWorkItem.activeRequestId = null;
        });
    });
  },
});

export const {
  clearResearchCompContext,
  clearResearchIntakePreview,
  clearSelectedResearchWorkItem,
  setResearchCompContext,
} = researchSlice.actions;

export default researchSlice;
